import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, fileUrl } from "../../lib/api";
import { SparkoLogo } from "../../components/SparkoLogo";
import { Icon } from "../../components/common";

const COLLECTIONS = {
  submissions: { label: "Requests", icon: "Inbox" },
  services: { label: "Services", icon: "Sparkles" },
  projects: { label: "Projects", icon: "Briefcase" },
  gallery: { label: "Gallery", icon: "Images" },
  reviews: { label: "Reviews", icon: "Star" },
  faq: { label: "FAQ", icon: "HelpCircle" },
  blog: { label: "Blog", icon: "PenSquare" },
  team: { label: "Team", icon: "Users" },
  settings: { label: "SEO & Settings", icon: "Settings" },
};

const SCHEMAS = {
  services: ["name_en", "name_nl", "name_ar", "desc_en", "desc_nl", "desc_ar", "category", "category_name", "slug", "icon", "image", "featured"],
  projects: ["title", "category", "location", "summary", "image"],
  gallery: ["title", "category", "image"],
  reviews: ["name", "role", "rating", "text"],
  faq: ["q_en", "q_nl", "a_en", "a_nl"],
  blog: ["title", "slug", "category", "author", "date", "excerpt", "content", "image"],
  team: ["name", "role", "image"],
};
const TEXTAREAS = ["desc_en", "desc_nl", "desc_ar", "text", "summary", "excerpt", "content", "a_en", "a_nl"];
const IMAGES = ["image"];
const NUMBERS = ["rating"];
const BOOLS = ["featured"];

const ImageField = ({ value, onChange }) => {
  const [up, setUp] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUp(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(data.url); toast.success("Uploaded"); }
    catch { toast.error("Upload failed"); }
    setUp(false);
  };
  return (
    <div className="flex items-center gap-3">
      {value ? <img src={fileUrl(value)} alt="" className="h-16 w-16 rounded-lg object-cover" /> : <div className="h-16 w-16 rounded-lg bg-slate-100 grid place-items-center text-slate-400"><Icon name="Image" size={20} /></div>}
      <div className="flex-1">
        <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" className="w-full rounded-lg border border-black/10 px-3 py-2 text-sm" />
        <label className="mt-1 inline-flex items-center gap-1.5 text-xs text-blue cursor-pointer">
          <Icon name={up ? "Loader2" : "Upload"} size={13} className={up ? "animate-spin" : ""} /> Upload
          <input type="file" accept="image/*" className="hidden" onChange={upload} />
        </label>
      </div>
    </div>
  );
};

const EditModal = ({ collection, item, onClose, onSaved }) => {
  const [form, setForm] = useState(() => { const f = {}; SCHEMAS[collection].forEach((k) => f[k] = item?.[k] ?? (BOOLS.includes(k) ? false : NUMBERS.includes(k) ? 5 : "")); return f; });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setSaving(true);
    try {
      if (item?.id) await api.put(`/content/${collection}/${item.id}`, form);
      else await api.post(`/content/${collection}`, form);
      toast.success("Saved"); onSaved(); onClose();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[90] grid place-items-center bg-navy/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="edit-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-navy">{item?.id ? "Edit" : "Add"} {COLLECTIONS[collection].label}</h3>
          <button onClick={onClose}><Icon name="X" size={22} /></button>
        </div>
        <div className="space-y-4">
          {SCHEMAS[collection].map((k) => (
            <div key={k}>
              <label className="text-sm font-medium text-navy capitalize">{k.replace(/_/g, " ")}</label>
              {IMAGES.includes(k) ? <div className="mt-1"><ImageField value={form[k]} onChange={(v) => set(k, v)} /></div>
                : BOOLS.includes(k) ? <div className="mt-1"><input type="checkbox" checked={!!form[k]} onChange={(e) => set(k, e.target.checked)} className="h-5 w-5" /></div>
                : NUMBERS.includes(k) ? <input type="number" min="1" max="5" value={form[k]} onChange={(e) => set(k, +e.target.value)} className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2" />
                : TEXTAREAS.includes(k) ? <textarea rows={3} value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2 resize-none" />
                : <input value={form[k]} onChange={(e) => set(k, e.target.value)} className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2" />}
            </div>
          ))}
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-full px-5 py-2.5 text-navy hover:bg-slate-100">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="modal-save" className="inline-flex items-center gap-2 rounded-full bg-blue px-6 py-2.5 font-semibold text-white disabled:opacity-60">
            {saving && <Icon name="Loader2" size={16} className="animate-spin" />} Save
          </button>
        </div>
      </div>
    </div>
  );
};

const ContentManager = ({ collection }) => {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = () => { setLoading(true); api.get(`/content/${collection}`).then((r) => setItems(r.data)).finally(() => setLoading(false)); };
  useEffect(load, [collection]);
  const del = async (id) => { if (!window.confirm("Delete this item?")) return; await api.delete(`/content/${collection}/${id}`); toast.success("Deleted"); load(); };
  const title = (it) => it.name_en || it.title || it.name || it.q_en || "Item";

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-2xl text-navy">{COLLECTIONS[collection].label} <span className="text-slate-400 text-lg">({items.length})</span></h2>
        <button onClick={() => setEditing({})} data-testid="add-item" className="inline-flex items-center gap-2 rounded-full bg-blue px-5 py-2.5 font-semibold text-white"><Icon name="Plus" size={17} /> Add</button>
      </div>
      {loading ? <div className="py-20 text-center"><Icon name="Loader2" size={28} className="animate-spin text-blue mx-auto" /></div> : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((it) => (
            <div key={it.id} className="rounded-2xl bg-white border border-black/5 overflow-hidden" data-testid="admin-item">
              {it.image && <img src={fileUrl(it.image)} alt="" className="h-32 w-full object-cover" />}
              <div className="p-4">
                <p className="font-semibold text-navy line-clamp-1">{title(it)}</p>
                <p className="text-sm text-slate-400 line-clamp-1">{it.category || it.role || it.category_name || ""}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => setEditing(it)} className="flex-1 rounded-lg bg-slate-100 py-1.5 text-sm font-medium text-navy hover:bg-slate-200 inline-flex items-center justify-center gap-1"><Icon name="Pencil" size={14} /> Edit</button>
                  <button onClick={() => del(it.id)} className="rounded-lg bg-red-50 px-3 py-1.5 text-red-600 hover:bg-red-100"><Icon name="Trash2" size={15} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {editing && <EditModal collection={collection} item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={load} />}
    </div>
  );
};

const STATUS = {
  new: "bg-blue/10 text-blue",
  contacted: "bg-amber-100 text-amber-700",
  "quote-sent": "bg-purple-100 text-purple-700",
  accepted: "bg-teal-100 text-teal-700",
  completed: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};
const STATUS_LABELS = { new: "New", contacted: "Contacted", "quote-sent": "Quote Sent", accepted: "Accepted", completed: "Completed", cancelled: "Cancelled" };
const STATUS_KEYS = ["new", "contacted", "quote-sent", "accepted", "completed", "cancelled"];
const Submissions = () => {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sel, setSel] = useState(null);
  const load = () => api.get(`/submissions?status=${status}&search=${encodeURIComponent(q)}`).then((r) => setItems(r.data));
  useEffect(() => { load(); }, [status]);
  const setStat = async (id, s) => { await api.patch(`/submissions/${id}`, { status: s }); toast.success("Updated"); load(); if (sel?.id === id) setSel({ ...sel, status: s }); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/submissions/${id}`); toast.success("Deleted"); setSel(null); load(); };
  const exportCsv = () => { const tk = localStorage.getItem("sparko_token"); window.open(`${api.defaults.baseURL}/submissions/export/csv?auth=${tk}`, "_blank"); };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h2 className="font-display text-2xl text-navy">Requests <span className="text-slate-400 text-lg">({items.length})</span></h2>
        <button onClick={exportCsv} data-testid="export-csv" className="inline-flex items-center gap-2 rounded-full border border-navy/15 px-5 py-2.5 font-semibold text-navy hover:bg-navy hover:text-white transition-colors"><Icon name="Download" size={16} /> Export CSV</button>
      </div>
      <div className="flex flex-wrap gap-3 mb-5">
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex-1 min-w-[200px] flex gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone..." data-testid="sub-search" className="flex-1 rounded-full border border-black/10 px-4 py-2.5" />
          <button className="rounded-full bg-navy px-4 text-white"><Icon name="Search" size={16} /></button>
        </form>
        <div className="flex flex-wrap gap-1 rounded-full bg-slate-100 p-1">
          {["all", ...STATUS_KEYS].map((s) => (
            <button key={s} onClick={() => setStatus(s)} data-testid={`sub-filter-${s}`} className={`rounded-full px-3 py-1.5 text-sm ${status === s ? "bg-white shadow text-navy font-semibold" : "text-slate-500"}`}>{s === "all" ? "All" : STATUS_LABELS[s]}</button>
          ))}
        </div>
      </div>
      <div className="overflow-x-auto rounded-2xl border border-black/5 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500"><tr>
            {["Name", "Type", "Service", "Phone", "Status", "Date", ""].map((h) => <th key={h} className="text-start p-3 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-t border-black/5 hover:bg-slate-50 cursor-pointer" onClick={() => setSel(s)} data-testid="submission-row">
                <td className="p-3 font-medium text-navy">{s.name}</td>
                <td className="p-3 capitalize">{s.type}</td>
                <td className="p-3 text-slate-500">{s.service || "-"}</td>
                <td className="p-3">{s.phone}</td>
                <td className="p-3"><span className={`rounded-full px-2.5 py-1 text-xs font-medium ${STATUS[s.status] || "bg-slate-100"}`}>{STATUS_LABELS[s.status] || s.status}</span></td>
                <td className="p-3 text-slate-400">{(s.created_at || "").slice(0, 10)}</td>
                <td className="p-3"><button onClick={(e) => { e.stopPropagation(); del(s.id); }} className="text-red-500"><Icon name="Trash2" size={15} /></button></td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-slate-400">No submissions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {sel && (
        <div className="fixed inset-0 z-[90] grid place-items-center bg-navy/60 p-4" onClick={() => setSel(null)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-6 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="submission-detail">
            <div className="flex items-center justify-between mb-4"><h3 className="font-display text-xl text-navy">{sel.name}</h3><button onClick={() => setSel(null)}><Icon name="X" size={22} /></button></div>
            <div className="space-y-2 text-sm">
              {[["Company", sel.company], ["Phone", sel.phone], ["Email", sel.email], ["Address", sel.address], ["Service", sel.service], ["Frequency", sel.frequency], ["Preferred Date", sel.preferred_date], ["Message", sel.message]].map(([k, v]) => (
                <div key={k} className="flex gap-3"><span className="w-32 text-slate-400 shrink-0">{k}</span><span className="text-navy">{v || "-"}</span></div>
              ))}
              {sel.photos?.length > 0 && <div className="flex gap-2 flex-wrap mt-2">{sel.photos.map((p, i) => <img key={i} src={fileUrl(p)} alt="" className="h-20 w-20 rounded-lg object-cover" />)}</div>}
            </div>
            <div className="mt-5 flex flex-wrap gap-2">
              {STATUS_KEYS.map((s) => (
                <button key={s} onClick={() => setStat(sel.id, s)} className={`rounded-full px-3 py-1.5 text-sm ${sel.status === s ? "bg-blue text-white" : "bg-slate-100 text-navy"}`}>{STATUS_LABELS[s]}</button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const Settings = () => {
  const [s, setS] = useState({});
  useEffect(() => { api.get("/settings").then((r) => setS(r.data || {})); }, []);
  const save = async () => { await api.put("/settings", s); toast.success("Settings saved"); };
  const fields = ["seo_title", "seo_description", "seo_keywords", "phone", "whatsapp", "email", "kvk", "address", "hours"];
  return (
    <div className="max-w-2xl">
      <h2 className="font-display text-2xl text-navy mb-6">SEO & Settings</h2>
      <div className="space-y-4">
        {fields.map((k) => (
          <div key={k}>
            <label className="text-sm font-medium text-navy capitalize">{k.replace(/_/g, " ")}</label>
            {k === "seo_description" ? <textarea rows={3} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2 resize-none" />
              : <input value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2" />}
          </div>
        ))}
      </div>
      <button onClick={save} data-testid="save-settings" className="mt-6 inline-flex items-center gap-2 rounded-full bg-blue px-6 py-2.5 font-semibold text-white"><Icon name="Save" size={16} /> Save</button>
    </div>
  );
};

const ChangePasswordModal = ({ onClose }) => {
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) { setErr("New password must be at least 8 characters"); return; }
    if (pw !== pw2) { setErr("New passwords do not match"); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: pw });
      toast.success("Password changed successfully");
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not change password");
    }
    setSaving(false);
  };
  return (
    <div className="fixed inset-0 z-[95] grid place-items-center bg-navy/60 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white p-6" onClick={(e) => e.stopPropagation()} data-testid="change-password-modal">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl text-navy">Change Password</h3>
          <button onClick={onClose}><Icon name="X" size={22} /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-sm font-medium text-navy">Current password</label>
            <input type="password" required value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current"
              className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2.5 outline-none focus:border-blue" placeholder="••••••••" />
          </div>
          <div>
            <label className="text-sm font-medium text-navy">New password</label>
            <input type="password" required value={pw} onChange={(e) => setPw(e.target.value)} data-testid="cp-new"
              className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2.5 outline-none focus:border-blue" placeholder="At least 8 characters" />
          </div>
          <div>
            <label className="text-sm font-medium text-navy">Confirm new password</label>
            <input type="password" required value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="cp-confirm"
              className="mt-1 w-full rounded-lg border border-black/10 px-3 py-2.5 outline-none focus:border-blue" placeholder="••••••••" />
          </div>
          {err && <p className="text-sm text-red-500" data-testid="cp-error">{err}</p>}
          <button type="submit" disabled={saving} data-testid="cp-submit"
            className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue px-6 py-3 font-semibold text-white hover:bg-[#0052FF] disabled:opacity-60">
            {saving ? <Icon name="Loader2" size={16} className="animate-spin" /> : <Icon name="ShieldCheck" size={16} />} Update Password
          </button>
        </form>
      </div>
    </div>
  );
};

export default function Dashboard() {
  const nav = useNavigate();
  const [tab, setTab] = useState("submissions");
  const [ok, setOk] = useState(false);
  const [showPw, setShowPw] = useState(false);
  useEffect(() => {
    if (!localStorage.getItem("sparko_token")) { nav("/admin/login"); return; }
    api.get("/auth/me").then(() => setOk(true)).catch(() => { localStorage.removeItem("sparko_token"); nav("/admin/login"); });
  }, [nav]);
  const logout = () => { localStorage.removeItem("sparko_token"); nav("/admin/login"); };
  if (!ok) return <div className="min-h-screen grid place-items-center bg-navy"><Icon name="Loader2" size={32} className="animate-spin text-blue" /></div>;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <aside className="w-64 shrink-0 bg-navy text-white flex-col hidden md:flex">
        <div className="p-6 border-b border-white/10"><SparkoLogo dark={false} size={26} /></div>
        <nav className="flex-1 p-3 space-y-1">
          {Object.entries(COLLECTIONS).map(([k, v]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
              className={`w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm transition-colors ${tab === k ? "bg-blue text-white" : "text-white/60 hover:bg-white/5"}`}>
              <Icon name={v.icon} size={18} /> {v.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <a href="/" className="w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-white/60 hover:bg-white/5"><Icon name="ExternalLink" size={18} /> View Site</a>
          <button onClick={() => setShowPw(true)} data-testid="change-password-btn" className="w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-white/60 hover:bg-white/5"><Icon name="KeyRound" size={18} /> Change Password</button>
          <button onClick={logout} data-testid="logout" className="w-full flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm text-white/60 hover:bg-white/5"><Icon name="LogOut" size={18} /> Logout</button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="md:hidden flex items-center justify-between bg-navy text-white p-4">
          <SparkoLogo dark={false} size={22} />
          <div className="flex items-center gap-2">
            <select value={tab} onChange={(e) => setTab(e.target.value)} className="bg-white/10 rounded-lg px-3 py-2 text-sm">
              {Object.entries(COLLECTIONS).map(([k, v]) => <option key={k} value={k} className="text-navy">{v.label}</option>)}
            </select>
            <button onClick={() => setShowPw(true)} data-testid="change-password-btn-mobile" className="p-2 rounded-lg bg-white/10" title="Change Password"><Icon name="KeyRound" size={18} /></button>
            <button onClick={logout} className="p-2 rounded-lg bg-white/10" title="Logout"><Icon name="LogOut" size={18} /></button>
          </div>
        </header>
        <main className="flex-1 p-6 md:p-10 overflow-auto">
          {tab === "submissions" ? <Submissions /> : tab === "settings" ? <Settings /> : <ContentManager collection={tab} />}
        </main>
      </div>
      {showPw && <ChangePasswordModal onClose={() => setShowPw(false)} />}
    </div>
  );
}
